/* eslint-disable @typescript-eslint/member-ordering */
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  linkedSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { ButtonComponent, PageHeaderComponent, SideSheetDirective, SideSheetHeaderComponent } from '@libs/common/components';
import {
  ConfigurableBundleAddToCartItemModel,
  ConfigurableBundleAddToCartModel,
  ConfigurableBundleFormStateModel,
  ConfigurableBundleFormStateSlotModel,
  ConfigurableBundleSlotModel,
  ConfigurableBundleTemplateModel,
  ProductBundleItemModel,
  ShopProductListItemBaseModel,
} from '@libs/shop/models';
import { ConfigurableBundleSummaryVisibleSections } from './api';
import { ConfigurableBundleFormBannerComponent } from './configurable-bundle-form-banner';
import { ConfigurableBundleFormBottomBarComponent } from './configurable-bundle-form-bottom-bar';
import { ConfigurableBundleFormSlotComponent } from './configurable-bundle-form-slot';
import { ConfigurableBundleFormSlotsInfoComponent } from './configurable-bundle-form-slots-info';
import { ConfigurableBundleFormSummaryComponent } from './configurable-bundle-form-summary';
import { ConfigurableBundleFormUiService } from './configurable-bundle-form-ui.service';

@Component({
  selector: 'ui-configurable-bundle-form',
  imports: [
    PageHeaderComponent,
    ConfigurableBundleFormBannerComponent,
    ConfigurableBundleFormSlotComponent,
    ConfigurableBundleFormSummaryComponent,
    ConfigurableBundleFormSlotsInfoComponent,
    ConfigurableBundleFormBottomBarComponent,
    SideSheetDirective,
    NgTemplateOutlet,
    SideSheetHeaderComponent,
    ButtonComponent,
  ],
  templateUrl: './configurable-bundle-form.component.html',
  styleUrl: './configurable-bundle-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigurableBundleFormComponent {
  private readonly uiService: ConfigurableBundleFormUiService = inject(ConfigurableBundleFormUiService);

  public bundleTemplate: InputSignal<ConfigurableBundleTemplateModel> = input.required();
  public cartItem: InputSignal<ShopProductListItemBaseModel | undefined> = input();
  public isAddToCartLoading: InputSignal<boolean> = input(false);
  private isBundleStateModifiedByUser: boolean = false;

  public bundleState: WritableSignal<ConfigurableBundleFormStateModel> = linkedSignal<
    { bundleTemplate: ConfigurableBundleTemplateModel; cartItem: ShopProductListItemBaseModel | undefined },
    ConfigurableBundleFormStateModel
  >({
    source: () => ({ bundleTemplate: this.bundleTemplate(), cartItem: this.cartItem() }),
    computation: ({ bundleTemplate }, previous) => {
      // Keep user modifications when only the cart changes; reset when a different template is shown.
      if (previous && this.isBundleStateModifiedByUser && previous.source.bundleTemplate.id === bundleTemplate.id) {
        return previous.value;
      }

      this.isBundleStateModifiedByUser = false;

      return this.validateAndAdjustBundleState({
        templateId: bundleTemplate.id,
        quantity: 1,
        slotMap: this.buildSlotMap(bundleTemplate.slots),
      });
    },
  });

  public freeSlotDisabled: Signal<boolean> = computed(() => {
    return this.bundleState().slotMap.values().toArray().filter(x => x.slotType === 'choice').some(x => x.thresholdStepReachedIndex === -1);
  });

  public addToCartDisabled: Signal<boolean> = computed(() => {
    const slotStates: ConfigurableBundleFormStateSlotModel[] = this.bundleState().slotMap.values().toArray();

    return slotStates.length <= 0
      || slotStates.some(x => x.thresholdStepReachedIndex === -1 && x.slotType !== 'mandatory');
  });

  public slotThresholdReachedIndex: Signal<number> = computed(() => this.computeSlotStateReachedIndex(this.bundleState()));

  public addToCart: OutputEmitterRef<ConfigurableBundleAddToCartModel> = output();

  public visibleSummarySections: WritableSignal<ConfigurableBundleSummaryVisibleSections[]> = signal(['bundle-summary', 'slot-step-summary']);

  public onAddToCart(): void {
    if (this.addToCartDisabled()) {
      return;
    }

    const configurableItems: ProductBundleItemModel[] = this.bundleState().slotMap.values().toArray()
      .flatMap(x => x.itemMap.values().toArray())
      .filter(x => !!x);

    const mandatoryItems: ProductBundleItemModel[] = this.bundleTemplate().slots.find(x => x.slotType === 'mandatory')?.products ?? [];

    const allItems: ProductBundleItemModel[] = mandatoryItems.concat(configurableItems);

    const payload: ConfigurableBundleAddToCartModel = {
      type: 'configurable-bundle-templates',
      id: this.bundleTemplate().id,
      quantity: this.bundleState().quantity,
      items: allItems.map(item => ({
        sku: item.sku,
        slotId: item.slotId ?? '',
        quantity: item.quantity,
      } satisfies ConfigurableBundleAddToCartItemModel)),
    };

    // The submitted state becomes the cart state, so the next cart change may recompute it again.
    this.isBundleStateModifiedByUser = false;
    this.addToCart.emit(payload);
  }

  public onSlotUpdate(state: ConfigurableBundleFormStateSlotModel): void {
    const slotMap: Map<string, ConfigurableBundleFormStateSlotModel> = new Map(this.bundleState().slotMap);

    slotMap.set(state.slotId, state);
    const newBundleState: ConfigurableBundleFormStateModel = this.validateAndAdjustBundleState({
      ...this.bundleState(),
      slotMap,
    });

    this.onBundleStateChange(newBundleState);
  }

  public onBundleStateChange(state: ConfigurableBundleFormStateModel): void {
    this.isBundleStateModifiedByUser = true;
    this.bundleState.set(state);
  }

  private buildSlotMap(slots: ConfigurableBundleSlotModel[]): Map<string, ConfigurableBundleFormStateSlotModel> {
    const map: Map<string, ConfigurableBundleFormStateSlotModel> = new Map();

    for (const slot of slots) {
      const selectedProducts: ProductBundleItemModel[] = this.cartItem()?.productData.bundle?.products?.filter(x => x.slotId === slot.id) ?? [];
      const selectedAmount: number = selectedProducts.reduce((amount, product) => amount + product.quantity, 0);
      const itemMap: Map<string, ProductBundleItemModel | undefined> =
        slot.slotType === 'mandatory' ?
          new Map<string, ProductBundleItemModel | undefined>([]) :
          new Map<string, ProductBundleItemModel | undefined>(selectedProducts.map(x => [x.sku, { ...x }]));

      map.set(slot.id, {
        slotId: slot.id,
        slotType: slot.slotType,
        itemMap: itemMap,
        selectedAmount,
        thresholdSteps: slot.slotAmountSteps,
        thresholdStepReachedIndex: slot.slotType === 'mandatory' ? -1 : this.uiService.findThresholdIndex(selectedAmount, slot.slotAmountSteps),
      });
    }

    return map;
  }

  private validateAndAdjustBundleState(bundleState: ConfigurableBundleFormStateModel): ConfigurableBundleFormStateModel {
    const slotStates: ConfigurableBundleFormStateSlotModel[] = bundleState.slotMap.values().toArray();
    const globalSlotStateReachedIndex: number = this.computeSlotStateReachedIndex(bundleState);

    for (const slotState of slotStates) {

      if (slotState.slotType === 'free' && slotState.thresholdStepReachedIndex > globalSlotStateReachedIndex) {
        slotState.itemMap = new Map();
        slotState.selectedAmount = 0;
        slotState.thresholdStepReachedIndex = -1;

        bundleState.slotMap.set(slotState.slotId, slotState);
      }
    }

    return this.autoSelectProductsInSlotsWithOnlyOneProduct(bundleState);
  }

  private autoSelectProductsInSlotsWithOnlyOneProduct(bundleState: ConfigurableBundleFormStateModel): ConfigurableBundleFormStateModel {
    const nonMandatorySlots: ConfigurableBundleSlotModel[] = this.bundleTemplate().slots.filter(x => x.slotType !== 'mandatory');
    const slotIdsWithMultipleProducts: string[] = nonMandatorySlots.filter(x => x.products.length > 1).map(x => x.id);
    const slotsWithOneProduct: ConfigurableBundleSlotModel[] = nonMandatorySlots.filter(x => x.products.length <= 1);

    const slotStates: ConfigurableBundleFormStateSlotModel[] = bundleState.slotMap.values().toArray();
    const slotsWithMultipleProductsAreValid: boolean = slotStates.some(x =>
      x.thresholdStepReachedIndex !== -1 &&
      x.slotType !== 'mandatory' &&
      slotIdsWithMultipleProducts.includes(x.slotId));

    const allThresholdIndexes: number[] = slotStates.filter(x => x.slotType === 'choice').map(x => x.thresholdStepReachedIndex);
    const threshHoldIndex: number = Math.min(...allThresholdIndexes);

    if (slotsWithOneProduct.length) {
      for (const slot of slotsWithOneProduct) {
        const state: ConfigurableBundleFormStateSlotModel | undefined = bundleState.slotMap.get(slot.id);

        if (!state) {
          continue;
        }

        const bundleItem: ProductBundleItemModel = slot.products[0];

        if (slotsWithMultipleProductsAreValid) {
          bundleItem.quantity = slot.slotAmountSteps[threshHoldIndex];
          state.itemMap.set(bundleItem.sku, bundleItem);
          state.thresholdStepReachedIndex = threshHoldIndex;
          state.selectedAmount = slot.slotAmountSteps[threshHoldIndex];
        } else {
          state.itemMap = new Map();
          state.thresholdStepReachedIndex = -1;
          state.selectedAmount = 0;
        }

        bundleState.slotMap.set(slot.id, state);
      }
    }

    return bundleState;
  }

  private computeSlotStateReachedIndex(bundleState: ConfigurableBundleFormStateModel): number {
    return Math.min(...bundleState.slotMap.values().toArray().filter(x => x.slotType === 'choice')
      .map(x => x.thresholdStepReachedIndex));
  }
}
